import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar, { ScrollIndicator } from "./Navbar";
import Footer from "./Footer";
import WhatsAppButton from "./WhatsAppButton";

const IMAGES = {
  heroBg: "/images/imageweb_1.jpg",
};

function formatPrice(price) {
  const value = Number(price || 0);
  return `Rp ${value.toLocaleString("id-ID")}`;
}

export default function Products({ isAdmin, AdminSection }) {
  const [activeCategory, setActiveCategory] = useState("SEMUA");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(
          "https://bum-desa-warga-dekat-bandung-production.up.railway.app/api/products",
        );
        const data = await res.json();

        let normalized = [];

        if (Array.isArray(data)) {
          normalized = data;
        } else if (Array.isArray(data.products)) {
          normalized = data.products;
        } else if (Array.isArray(data.data)) {
          normalized = data.data;
        }

        setProducts(normalized);
      } catch (err) {
        console.error("Gagal ambil produk:", err);
        setError("Gagal mengambil data produk.");
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const categories = useMemo(() => {
    const dynamicCategories = Array.isArray(products)
      ? [
          ...new Set(
            products
              .map((p) => (p.category || "").toString().trim().toUpperCase())
              .filter(Boolean),
          ),
        ]
      : [];

    return ["SEMUA", ...dynamicCategories];
  }, [products]);

  const filteredProducts = useMemo(() => {
    if (!Array.isArray(products)) return [];

    if (activeCategory === "SEMUA") return products;

    return products.filter(
      (p) =>
        (p.category || "").toString().trim().toUpperCase() === activeCategory,
    );
  }, [products, activeCategory]);

  return (
    <div className="min-h-screen bg-white font-serif">
      <style>{`
        .page-hero {
          height: 420px;
          min-height: 320px;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          background-size: cover;
          background-position: center;
          overflow: hidden;
        }
        .page-hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            160deg,
            rgba(20, 40, 28, 0.75) 0%,
            rgba(61, 79, 69, 0.52) 60%,
            rgba(15, 30, 20, 0.82) 100%
          );
        }
        .page-hero-content {
          position: relative;
          z-index: 10;
          text-align: center;
          padding: 64px 1.5rem 0;
          animation: pgFadeUp 0.9s ease 0.15s both;
        }
        @keyframes pgFadeUp {
          from {
            opacity: 0;
            transform: translateY(24px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .card-wrap-hover .card-img {
          transition: transform 0.45s ease;
        }
        .card-wrap-hover:hover .card-img {
          transform: scale(1.05);
        }
      `}</style>

      <Navbar />
      <WhatsAppButton />

      <section
        className="page-hero"
        style={{ backgroundImage: `url(${IMAGES.heroBg})` }}
      >
        <div className="page-hero-overlay" />
        <div className="page-hero-content">
          <p
            style={{
              fontSize: ".62rem",
              letterSpacing: ".32em",
              color: "#b8c5ba",
              textTransform: "uppercase",
              marginBottom: "1rem",
            }}
          >
            BUM Desa Warga Dekat Bandung
          </p>

          <h1
            style={{
              fontSize: "clamp(2rem,4.5vw,3.4rem)",
              color: "#fff",
              fontWeight: 300,
              lineHeight: 1.18,
              marginBottom: "1rem",
            }}
          >
            Produk Kami
          </h1>

          <p
            style={{
              fontSize: ".87rem",
              color: "rgba(255,255,255,.82)",
              maxWidth: "520px",
              margin: "0 auto",
              lineHeight: 1.75,
            }}
          >
            Berbagai produk berkualitas hasil karya masyarakat desa, dari
            ladang, tangan, hingga dapur warga.
          </p>
        </div>

        <ScrollIndicator targetId="products-section" />
      </section>

      <section id="products-section" className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl text-center mb-2 text-[#3d4f45] font-light">
            Koleksi Produk
          </h2>

          <p className="text-center text-gray-500 text-sm mb-8">
            Produk unggulan dari BUM Desa yang dihasilkan dengan penuh dedikasi.
          </p>

          <div className="flex justify-center gap-3 mb-12 flex-wrap">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCategory(c)}
                className={`px-6 py-2 rounded-full text-xs tracking-wider transition ${
                  activeCategory === c
                    ? "bg-[#3d4f45] text-white"
                    : "bg-white border border-gray-300 text-gray-600 hover:bg-gray-50"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-center text-gray-500">Loading produk...</p>
          ) : error ? (
            <p className="text-center text-red-500">{error}</p>
          ) : filteredProducts.length === 0 ? (
            <p className="text-center text-gray-500">Produk belum ada.</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-8">
              {filteredProducts.map((p) => (
                <div
                  key={p.id}
                  className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition cursor-pointer card-wrap-hover"
                  onClick={() => navigate(`/products/${p.id}`)}
                >
                  <div className="h-64 overflow-hidden bg-[#b8c5ba] flex items-center justify-center">
                    {p.image_url || p.img ? (
                      <img
                        src={p.image_url || p.img}
                        alt={p.name || "Produk"}
                        className="card-img w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                    ) : (
                      <span className="text-sm text-gray-500">
                        Gambar belum tersedia
                      </span>
                    )}
                  </div>

                  <div className="p-6">
                    <div className="text-xs text-gray-400 mb-2 tracking-wider">
                      {(p.category || "PRODUK").toUpperCase()}
                    </div>

                    <h3 className="text-xl mb-3 text-[#3d4f45]">
                      {p.name || "Tanpa Nama"}
                    </h3>

                    <p className="text-[#3d4f45] text-sm font-semibold mb-3">
                      {formatPrice(p.price)}
                    </p>

                    <p className="text-gray-500 text-sm leading-relaxed mb-5 line-clamp-3">
                      {p.description || "Tidak ada deskripsi."}
                    </p>

                    <button className="bg-[#3d4f45] text-white px-6 py-2 rounded-full text-xs tracking-wider hover:bg-[#4a5a50] transition">
                      DETAIL PRODUK
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-16 px-6 bg-[#f5f7f6]">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="text-2xl mb-4 text-[#3d4f45] font-light">
            Informasi Pemesanan
          </h3>

          <p className="text-gray-600 text-sm leading-relaxed mb-3">
            Semua produk kami dibuat dengan bahan berkualitas tinggi dan
            dikerjakan oleh pelaku usaha lokal yang terampil.
          </p>

          <p className="text-gray-600 text-sm leading-relaxed mb-8">
            Untuk informasi lebih lanjut tentang pemesanan, harga, atau
            ketersediaan produk, silakan hubungi kami.
          </p>

          <Link
            to="/contact"
            className="inline-block bg-[#3d4f45] text-white px-8 py-3 rounded-full text-sm hover:bg-[#4a5a50] transition"
          >
            HUBUNGI KAMI
          </Link>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 pb-8">
        <hr className="my-8 border-gray-200" />
        <AdminSection />
      </div>

      <Footer />
    </div>
  );
}
